import React, { useState, useEffect } from 'react';
import './StaffDetail.css';
import { axiosInstance } from '../config';

const StaffDetail = ({ staffId, token, onClose, environment }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStaffDetails = async () => {
      setLoading(true);
      try {
        // Always fetch fresh data when component is opened
        const response = await axiosInstance.get(`/api/staff/details/${staffId}`);
        setDetails(response.data);
        
        // Get the most recent license check from the response
        if (response.data.licenseChecks && response.data.licenseChecks.length > 0) {
          // Sort by check_date in descending order
          const sortedChecks = [...response.data.licenseChecks].sort((a, b) => 
            new Date(b.check_date) - new Date(a.check_date)
          );
          
          setDetails(prevDetails => ({
            ...prevDetails,
            selectedCheck: sortedChecks[0]
          }));
        }
      } catch (error) {
        console.error('Error fetching staff details:', error);
        setError('Failed to load staff details');
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDetails();
  }, [staffId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const handleDoneClick = async () => {
    try {
      // ... your existing done button logic ...

      // After successful completion, wait 1 second then refresh the entire page
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) return <div className="staff-detail-modal">Loading...</div>;
  if (error) return <div className="staff-detail-modal">Error: {error}</div>;
  if (!details) return null;

  // Get the most recent license check's raw data
  const latestCheck = details.licenseChecks?.[0];
  const rawDvlaData = latestCheck?.response_data ? 
    (typeof latestCheck.response_data === 'string' ? 
      JSON.parse(latestCheck.response_data) : 
      latestCheck.response_data
    ) : null;

  // Get the common expiry date from vehicle categories (if they all match)
  const categoryExpiryDates = rawDvlaData?.vehicle_categories?.map(cat => cat.validUntil);
  const commonExpiryDate = categoryExpiryDates?.length > 0 && 
      categoryExpiryDates.every(date => date === categoryExpiryDates[0]) ? 
      categoryExpiryDates[0] : null;

  // Add debug logging
  console.log('Raw DVLA Data:', {
    token: rawDvlaData?.token,
    entitlement: rawDvlaData?.entitlement,
    responseData: latestCheck?.response_data
  });

  // Add this near your existing console.logs
  console.log('License check details:', {
    checkDate: latestCheck?.check_date,
    licenseValidFrom: latestCheck?.license_valid_from,
    licenseValidTo: latestCheck?.license_valid_to,
    rawDataType: typeof latestCheck?.response_data,
    rawData: latestCheck?.response_data
  });

  return (
    <div className="staff-detail-modal" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>Staff Member Details</h2>
        
        <div className="personal-info">
          <h3>Personal Information</h3>
          <table>
            <tbody>
              <tr>
                <th>Full Name</th>
                <td>{`${details.first_name} ${details.last_name}`}</td>
              </tr>
              <tr>
                <th>License Number</th>
                <td>{details.driving_license_number}</td>
              </tr>
              <tr>
                <th>Date of Birth</th>
                <td>{formatDate(details.date_of_birth)}</td>
              </tr>
              <tr>
                <th>Gender</th>
                <td>{details.gender || 'Not specified'}</td>
              </tr>
              <tr>
                <th>Card Expiry Date</th>
                <td>{formatDate(latestCheck?.license_valid_to) || 'N/A'}</td>
              </tr>
              {commonExpiryDate && (
                <tr>
                  <th>License Entitlement Valid Until</th>
                  <td>{formatDate(commonExpiryDate)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Check if any address field exists before rendering the address section */}
        {(details.address_line1 || details.address_line2 || details.address_line3 || 
          details.address_line4 || details.address_line5 || details.postcode || details.address) && (
          <div className="address-info">
            <h3>Address Information</h3>
            <table>
              <tbody>
                {details.address_line1 && (
                  <tr>
                    <th>Address Line 1</th>
                    <td>{details.address_line1}</td>
                  </tr>
                )}
                {details.address_line2 && (
                  <tr>
                    <th>Address Line 2</th>
                    <td>{details.address_line2}</td>
                  </tr>
                )}
                {details.address_line3 && (
                  <tr>
                    <th>Address Line 3</th>
                    <td>{details.address_line3}</td>
                  </tr>
                )}
                {details.address_line4 && (
                  <tr>
                    <th>Address Line 4</th>
                    <td>{details.address_line4}</td>
                  </tr>
                )}
                {details.address_line5 && (
                  <tr>
                    <th>Address Line 5</th>
                    <td>{details.address_line5}</td>
                  </tr>
                )}
                {details.postcode && (
                  <tr>
                    <th>Postcode</th>
                    <td>{details.postcode}</td>
                  </tr>
                )}
                {details.address && (
                  <tr>
                    <th>Full Address</th>
                    <td>{details.address}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {details.licenseChecks && details.licenseChecks.length > 0 && (
          <div className="license-checks">
            <h3>License Check History</h3>
            <table>
              <thead>
                <tr>
                  <th>Check Date</th>
                  <th>Status</th>
                  <th>Points</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                </tr>
              </thead>
              <tbody>
                {details.licenseChecks.map((check, index) => (
                  <tr key={index}>
                    <td>{formatDate(check.check_date)}</td>
                    <td>{check.status}</td>
                    <td>{check.points_total}</td>
                    <td>{formatDate(check.license_valid_from)}</td>
                    <td>{formatDate(check.license_valid_to)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rawDvlaData?.endorsements && rawDvlaData.endorsements.length > 0 && (
          <div className="endorsements">
            <h3>Endorsements</h3>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Points</th>
                  <th>Offense Date</th>
                  <th>Conviction Date</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {rawDvlaData.endorsements.map((endorsement, index) => (
                  <tr key={index}>
                    <td>{endorsement.offenceCode}</td>
                    <td>{endorsement.penaltyPoints}</td>
                    <td>{formatDate(endorsement.offenceDate)}</td>
                    <td>{formatDate(endorsement.convictionDate)}</td>
                    <td>{endorsement.offenceLegalLiteral}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rawDvlaData?.vehicle_categories && rawDvlaData.vehicle_categories.length > 0 && (
          <div className="vehicle-categories">
            <h3>Vehicle Categories</h3>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Valid Until</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {rawDvlaData.vehicle_categories.map((category, index) => (
                  <tr key={index}>
                    <td>{category.code}</td>
                    <td>{category.type}</td>
                    <td>{formatDate(category.validUntil)}</td>
                    <td>
                      <div title={category.legal_literal} style={{ cursor: 'help' }}>
                        {category.legal_literal.length > 100 
                          ? `${category.legal_literal.substring(0, 100)}...` 
                          : category.legal_literal}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button onClick={handleDoneClick}>Done</button>
      </div>
    </div>
  );
};

export default StaffDetail; 